import { useDispatch, useSelector } from "react-redux";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import DeleteIcon from "@mui/icons-material/Delete";
import { red } from "@mui/material/colors";
import {
  Grid,
  Button,
  ButtonGroup,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  setRemoveAssetModalOptions,
  getCloneAsset,
  makeAssetPrimary,
  userDetailsSelector,
} from "src/store/slices/applicationFormSlice";

const AssetActionsLoader = () => {
  return <CircularProgress size="15px" style={{ margin: "4px" }} />;
};

const AssetActions = ({ index, asset, isMaxAssetsReached }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(userDetailsSelector);
  const assetId = asset._id;
  const applicationId = userDetails?.application?._id;
  const {
    cloneAssetLoaderOptions,
    makeAssetPrimaryLoaderOptions,
    removeAssetModalOptions,
  } = userDetails;

  const cloneAssetLoader =
    cloneAssetLoaderOptions?.isLoading &&
    cloneAssetLoaderOptions?.assetId === assetId;
  const makeAssetPrimaryLoader =
    makeAssetPrimaryLoaderOptions?.isLoading &&
    makeAssetPrimaryLoaderOptions?.assetId === assetId;
  const removeAssetLoader =
    removeAssetModalOptions?.isRemovingAsset &&
    removeAssetModalOptions?.assetId === assetId;

  const isDisabledCloneAsset = isMaxAssetsReached || cloneAssetLoader;

  const handle = {
    cloneAsset: () => {
      if (isDisabledCloneAsset) return;
      dispatch(getCloneAsset({ assetId, applicationId }));
    },
    makeAssetPrimary: () => {
      const makeAssetPrimaryPayload = {
        applicationId,
        assetId: assetId,
        order: index,
      };

      dispatch(makeAssetPrimary(makeAssetPrimaryPayload));
    },
    removeAsset: async () => {
      dispatch(
        setRemoveAssetModalOptions({
          isOpen: true,
          assetType: asset.assetType,
          assetId,
        })
      );
    },
  };

  const defaultButtonStyle = {
    "&:hover": { backgroundColor: "#f5f5f5" },
    borderColor: "#e0e0e0",
    padding: "4px 8px",
  };

  const defaultTooltipStyle = {
    placement: "bottom",
    size: "sm",
  };

  const makePrimaryButtonTitle =
    index === 0 ? "Primary asset" : "Set as Primary";

  return (
    <Stack direction="row" justifyContent="flex-end">
      <ButtonGroup
        variant="outlined"
        color="primary"
        sx={{
          width: "100%",
          "& .MuiButton-root": {
            border: "none",
            borderRadius: 0,
          },
          "& .MuiButtonGroup-grouped:not(:last-of-type)": {
            borderRight: "1px solid rgba(239, 145, 145, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Grid
          container
          alignItems="center"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            "& > .MuiGrid-item": {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textTransform: "none",
              "&:not(:last-of-type)": {
                borderRight: "1px solid rgba(239, 145, 145, 0.2)",
              },
            },
            "& > .MuiGrid-item:hover": {
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
            },
          }}
        >
          <Grid item xs={6}>
            <Button
              variant="secondary"
              intent="neutral"
              size="small"
              disabled={makeAssetPrimaryLoader || index === 0}
              onClick={handle.makeAssetPrimary}
              style={{
                ...defaultButtonStyle,
                fontSize: "10px",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
                marginRight: "5px",
                padding: "4px 8px",
                minWidth: "95px",
              }}
            >
              {makeAssetPrimaryLoader ? "Saving..." : makePrimaryButtonTitle}
            </Button>
          </Grid>
          <Grid item lg={3}>
            <Tooltip title="Clone" {...defaultTooltipStyle}>
              <Button
                variant="secondary"
                intent="neutral"
                disabled={isDisabledCloneAsset}
                onClick={handle.cloneAsset}
                style={{ ...defaultButtonStyle, color: "#125984", ...(isDisabledCloneAsset && { opacity: 0.5 }) }}
              >
                {cloneAssetLoader ? (
                  <AssetActionsLoader />
                ) : (
                  <ContentCopyTwoToneIcon style={{ fontSize: "20px" }} />
                )}
              </Button>
            </Tooltip>
          </Grid>
          <Grid item lg={3}>
            <Tooltip title="Delete" {...defaultTooltipStyle}>
              <Button
                variant="secondary"
                intent="neutral"
                disabled={removeAssetLoader || index === 0}
                onClick={handle.removeAsset}
                style={{
                  ...defaultButtonStyle,
                  ...(index !== 0 && { color: red[500] }),
                }}
              >
                <DeleteIcon style={{ fontSize: "20px" }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </ButtonGroup>
    </Stack>
  );
};

export default AssetActions;
